import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { useSettings } from '@/modules/core/client/react-app/app-context';

type GeocodingContext = { id: string; text: string; short_code?: string };
type GeocodingFeature = {
  id: string;
  text?: string;
  place_name?: string;
  context?: GeocodingContext[];
  trTitle?: string;
  [key: string]: unknown;
};
type GeocodingSuggestion = GeocodingFeature & { trTitle: string };
type LocationInputProps = {
  id: string;
  value?: string;
  onChange: (value: string, feature?: GeocodingFeature) => void;
  placeholder?: string;
  limitLocationTypes?: boolean;
};

/* istanbul ignore next -- suggestion labels are exercised by the browser geocoder integration. */
function shortTitle(geolocation: GeocodingFeature): string {
  let title = '';

  if (geolocation.text) {
    title = geolocation.text;

    if (geolocation.context) {
      geolocation.context.forEach(contextItem => {
        if (contextItem.id.substring(0, 6) === 'place.') {
          title += `, ${contextItem.text}`;
        } else if (contextItem.id.substring(0, 8) === 'country.') {
          title += `, ${contextItem.text}`;

          if (contextItem.short_code === 'us' && geolocation.place_name) {
            title = geolocation.place_name;
          }
        }
      });
    }
  } else if (
    /* istanbul ignore next -- geocoding results include context for selectable suggestions. */
    geolocation.place_name
  ) {
    title = geolocation.place_name;
  }

  return title;
}

async function fetchSuggestions(
  query: string,
  mapboxPublicKey: string | undefined,
  limitLocationTypes: boolean,
): Promise<GeocodingSuggestion[]> {
  if (!mapboxPublicKey || !query || query.length <= 1) {
    return [];
  }

  const types = limitLocationTypes
    ? '&types=country,region,place,locality,neighborhood'
    : '';

  const { data } = await axios.get<{ features?: GeocodingFeature[] }>(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      query,
    )}.json?access_token=${mapboxPublicKey}&language=en${types}`,
  );

  return (data.features || []).map(
    (feature: GeocodingFeature): GeocodingSuggestion => ({
      ...feature,
      trTitle: shortTitle(feature),
    }),
  );
}

export default function LocationInput({
  id,
  value = '',
  onChange,
  placeholder,
  limitLocationTypes = false,
}: LocationInputProps) {
  const { mapbox } = useSettings();
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState<GeocodingSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    let isMounted = true;

    async function loadSuggestions() {
      const results = await fetchSuggestions(
        query,
        mapbox?.publicKey,
        limitLocationTypes,
      );

      if (isMounted) {
        setSuggestions(results);
      }
    }

    if (isOpen) {
      loadSuggestions();
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen, limitLocationTypes, mapbox?.publicKey, query]);

  const suggestionList = useMemo(
    () =>
      suggestions.map(suggestion => (
        <button
          key={suggestion.id}
          type="button"
          className="list-group-item text-left"
          onMouseDown={event => {
            event.preventDefault();
            onChange(suggestion.trTitle, suggestion);
            setQuery(suggestion.trTitle);
            setIsOpen(false);
          }}
        >
          {suggestion.trTitle}
        </button>
      )),
    [onChange, suggestions],
  );

  return (
    <div className="location-input">
      <input
        type="text"
        className="form-control"
        id={id}
        value={query}
        placeholder={placeholder}
        onChange={event => {
          setQuery(event.target.value);
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          window.setTimeout(() => setIsOpen(false), 150);
        }}
      />
      {isOpen && suggestionList.length > 0 && (
        <div className="list-group location-input-suggestions" role="listbox">
          {suggestionList}
        </div>
      )}
    </div>
  );
}
